import { toggleMark } from 'prosemirror-commands';
import {
  DOMParser,
  DOMSerializer,
  Fragment,
  MarkType,
  NodeType,
  Slice,
} from 'prosemirror-model';
import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import { Command, EditorState, Transaction } from 'prosemirror-state';
import { useState } from 'react';
import { Link } from './editor/components/Link/LinkPrompt';
import plugins from './editor/plugins';
import schema from './editor/schema';
import { getHTMLStringFromState } from './editor/utils';

/* modifier check */
export const isMarkActive = (state: EditorState, mark: MarkType): boolean => {
  const { from, $from, to, empty } = state.selection;
  return empty
    ? !!mark.isInSet(state.storedMarks || $from.marks())
    : state.doc.rangeHasMark(from, to, mark);
};

// https://discuss.prosemirror.net/t/list-type-toggle/948/2
export const isBlockActive = (state: EditorState, type: NodeType) => {
  const { $from, $to, to } = state.selection;

  const range = $from.blockRange($to);

  return (
    !!range?.depth &&
    $from
      .node(range.depth > 1 ? range.depth - 1 : range.depth)
      ?.hasMarkup?.(type)
  );
};

export const isBold = (state: EditorState): boolean =>
  isMarkActive(state, schema.marks.strong);

export const isItalic = (state: EditorState): boolean =>
  isMarkActive(state, schema.marks.em);

export const isUnderline = (state: EditorState): boolean =>
  isMarkActive(state, schema.marks.underline);

export const isStrikethrough = (state: EditorState): boolean =>
  isMarkActive(state, schema.marks.strikethrough);

const isLink = (state: EditorState): boolean =>
  isMarkActive(state, schema.marks.link);

const isOrderedList = (state: EditorState): boolean =>
  isBlockActive(state, schema.nodes.ordered_list);

const isUnorderedList = (state: EditorState): boolean =>
  isBlockActive(state, schema.nodes.bullet_list);

/* toggle commands.ts */
const toggleMarkCommand =
  (mark: MarkType): Command =>
  (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) =>
    toggleMark(mark)(state, dispatch);

const toggleUnorderedList = (state: EditorState, dispatch) => {
  const isActive = isUnorderedList(state);
  if (isActive) {
    // https://discuss.prosemirror.net/t/toggle-bullet-list-is-not-working/1714/2
    liftListItem(schema.nodes.list_item)(state, dispatch);
    return;
  }

  wrapInList(schema.nodes.bullet_list)(state, dispatch);
};

const toggleOrderedList = (state: EditorState, dispatch) => {
  const isActive = isOrderedList(state);
  if (isActive) {
    // https://discuss.prosemirror.net/t/toggle-bullet-list-is-not-working/1714/2
    liftListItem(schema.nodes.list_item)(state, dispatch);
    return;
  }

  wrapInList(schema.nodes.ordered_list)(state, dispatch);
};

const toggleBold = toggleMarkCommand(schema.marks.strong);

const toggleItalic = toggleMarkCommand(schema.marks.em);

const toggleUnderline = toggleMarkCommand(schema.marks.underline);

const toggleStrikethrough = toggleMarkCommand(schema.marks.strikethrough);

const toggleLink = toggleMarkCommand(schema.marks.link);

/*
  react state related code, when time allow get rid of this connection
  and preserve only main state in react context
  to let internal components in editor access it
 */
export const useEditor = (value: string) => {
  const NON_EMPTY_HREF_TITLE_FLAG = 'link';

  const [el] = useState<HTMLDivElement>(() => {
    const el = document.createElement('div');
    el.innerHTML = value;
    return el;
  });

  const [doc, setDoc] = useState(() => DOMParser.fromSchema(schema).parse(el));

  const [state, setState] = useState(() =>
    EditorState.create({
      schema,
      doc,
      plugins,
    }),
  );

  const insertText: typeof schema.text = (text, marks) => {
    const node = schema.text(text, marks);
    const frag = Fragment.from(node);
    const slice = new Slice(frag, 0, 0);
    const t = state.tr.replaceRange(
      state.selection.from,
      state.selection.to,
      slice,
    );
    const st = state.apply(t);
    setState(st);

    return node;
  };

  const extractEmailBody = (text: string) => {
    const startIndex = text.indexOf('\n\n') + 1;
    if (startIndex !== -1) {
      return text.substring(startIndex).trim();
    }
  };

  const insertParagraphs = (text: string, extractSubject = true) => {
    const extractedTextWithoutSubject = extractSubject
      ? extractEmailBody(text)
      : text;

    const paragraphs = extractedTextWithoutSubject
      ?.split('\n\n')
      .map(line => schema.nodes.paragraph.create(null, schema.text(line)));

    const fragment = Fragment.from(paragraphs);
    const slice = new Slice(fragment, 0, 0);
    const transaction = state.tr.replaceRange(
      state.selection.from,
      state.selection.to,
      slice,
    );
    const newState = state.apply(transaction);
    setState(newState);

    return paragraphs;
  };

  const insertLink = (link: Link) => {
    if (link.title) {
      insertText(link.title, [schema.marks.link.create(link)]);
    }
  };

  // https://github.com/ProseMirror/prosemirror-example-setup/blob/afbc42a68803a57af3f29dd93c3c522c30ea3ed6/src/menu.js#L57-L61
  const [link, setLink] = useState<Link | null>();

  const openLink = (state: EditorState, dispatch) => {
    if (isLink(state)) {
      toggleLink(state, dispatch);
      return;
    }

    setLink({
      title: state.selection.empty ? '' : NON_EMPTY_HREF_TITLE_FLAG,
    });
  };

  const closeLink = () => setLink(null);

  const updateLink = (state, dispatch, link: Link) => {
    if (link.title !== NON_EMPTY_HREF_TITLE_FLAG) {
      insertLink(link);
    } else {
      toggleMark(schema.marks.link, link)(state, dispatch);
    }

    setLink(null);
  };

  const canInsertImageAtCursor = (state: EditorState) => {
    const { $from } = state.selection;
    for (let d = $from.depth; d >= 0; d -= 1) {
      const index = $from.index(d);
      if ($from.node(d).canReplaceWith(index, index, schema.nodes.image)) {
        return true;
      }
    }
    return false;
  };

  return {
    doc,
    setDoc,
    state,
    setState,
    isBold,
    isItalic,
    isLink,
    isOrderedList,
    isUnorderedList,
    isUnderline,
    isStrikethrough,
    toggleUnderline,
    toggleStrikethrough,
    toggleBold,
    toggleItalic,
    toggleLink: openLink,
    toggleUnorderedList,
    toggleOrderedList,
    closeLink,
    updateLink,
    link,
    insertText,
    insertParagraphs,
    getHTMLStringFromState,
    canInsertImageAtCursor,
  };
};
